import { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import TableWrapper from '@/components/atoms/TableWrapper';
import CustomTable from '@/components/atoms/CustomTable';
import { Filters } from './Filters';
import {
  setTotalRevenueFilters,
  useTotalRevenueFilters,
} from '@/store/slices/reportFiltersSlice';
import { RequestDetailsDrawer } from './RequestDetailsDrawer';
import { useGetTotalRevenueReportQuery } from '@/store/services/reports';
import {
  setModalId,
  setPage,
  usePublicState,
} from '@/store/slices/publicSlice';
import Helpers from '@/lib/helpers';

export interface TotalRevenueItemType {
  transactionId?: string;
  requestBudget?: number;
  requestCommission?: number;
  requestType?: string;
  requestDate?: string;
  serial?: number | string;
  orderId?: string;
  orderSerial?: string;
  orderMethod?: string;
  id?: string | number;
  [key: string]: any;
}

export interface TotalRevenueTableRow {
  id: string;
  transactionId: string;
  requestBudget: number;
  requestComm: number;
  requestType: string;
  requestDate: string;
}

const RequestsTable = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { search, page } = usePublicState();
  const filters = useTotalRevenueFilters();
  const dispatch = useDispatch();

  const searchValue = search?.revenueReportSearch || '';

  const {
    data: totalRevenue,
    isLoading,
    isFetching,
  } = useGetTotalRevenueReportQuery(
    {
      page: page,
      dateFrom: filters?.fromDate,
      dateTo: filters?.toDate,
      requestTypes: filters?.requestTypes || filters?.requestTypes,
      search: searchValue,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !filters?.fromDate && !filters?.toDate,
    }
  );

  const columns: TableProps<TotalRevenueTableRow>['columns'] = [
    {
      title: t('reports.transactionId', 'Transaction id'),
      dataIndex: 'transactionId',
      key: 'transactionId',
      render: (val) => val || '-',
    },
    {
      title: t('reports.requestBudget', 'Request budget'),
      dataIndex: 'requestBudget',
      key: 'requestBudget',
      render: (val) =>
        val !== undefined && val !== null ? Helpers.formatMoney(val) : '-',
      minWidth: 170,
    },
    {
      title: t('reports.requestComm', 'Request comm.'),
      dataIndex: 'requestComm',
      key: 'requestComm',
      render: (val) =>
        val !== undefined && val !== null ? Helpers.formatMoney(val) : '-',
      minWidth: 150,
    },
    {
      title: t('reports.requestType', 'Request type'),
      dataIndex: 'requestType',
      key: 'requestType',
      render: (val) => val || '-',
      minWidth: 150,
    },
    {
      title: t('reports.requestDate', 'Request date'),
      key: 'requestDate',
      dataIndex: 'requestDate',
      render: (value) => {
        return (
          <p>
            {dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : '-'}
          </p>
        );
      },
    },
  ];

  dayjs.locale(language);

  return (
    <>
      <TableWrapper
        filters={<Filters />}
        title={t('reports.requests', 'Requests')}
        subTitle={t(
          'reports.revenue.subTitle',
          'Display platform total revenue'
        )}
      >
        <CustomTable<TotalRevenueTableRow>
          rowKey="id"
          loading={isLoading || isFetching}
          currentPage={page || 0}
          pageSize={filters?.limit || 10}
          totalElements={totalRevenue?.payload?.totalElements || 0}
          onPageChange={(pageNumber, pageSize) => {
            dispatch(setPage(pageNumber - 1));
            dispatch(
              setTotalRevenueFilters({
                limit: pageSize,
              })
            );
          }}
          onRowClick={(_, record) => {
            const id = record.id;
            dispatch(setModalId({ id }));
          }}
          columns={columns}
          dataSource={
            totalRevenue?.payload?.content?.map((item: any) => ({
              id: String(item?.transactionId || item?.id || item?.serial || ''),
              transactionId: item?.serial,
              requestBudget: item?.requestBudget,
              requestComm: item?.requestCommission,
              requestType: item?.requestType,
              requestDate: item?.requestDate,
            })) || []
          }
        />
      </TableWrapper>

      <RequestDetailsDrawer
        listOfIds={
          (totalRevenue?.payload?.content
            ?.map((el: any) =>
              String(el?.transactionId || el?.id || el?.serial || '')
            )
            .filter(Boolean) as string[]) || []
        }
      />
    </>
  );
};

export default RequestsTable;
